import React, {Fragment, useContext, useEffect, useMemo, useState} from "react";
import DropdownListForm, {DropdownListItem} from "../../components/forms/dropdownListForm";
import Form from "../../components/forms/form";
import RadioForm from "../../components/forms/radioForm";
import ButtonForm from "../../components/forms/buttonForm";
import {Language, TimeFormat} from "../../util/enum";
import TemplateEditContentList, {TemplateContent} from "./templateEditContentList";
import CalendarPicker from "../../components/datePicker/calendarPicker";
import moment from "moment-timezone";
import SendTestingMessageModal from "./sendTestingMessageModal";
import {
    BeforeDayForAutomatedCampaign,
    CampaignSetting,
    dataFoAppointmentReminder,
    dataForPostAppointment,
    dataFoRecurrence,
    dataForFireType,
    dataForIsRecurring,
    dataForMessagePerDay,
    FireType,
    getDefaultAppointmentDataForTemplateLanguage,
    getDefaultAppointmentReminderTemplates,
    getDefaultCampaignTemplates,
    getDefaultDataForTemplateLanguage,
    koreanClinics,
    predefinedTemplateLanguages,
    validateDateTime,
} from "./dataForDesignCampaign";
import DateTimePicker from "../../components/datePicker/dateTimePicker";
import Spinner from "../../components/spinner/spinner";
import {
    getTemplateList,
    MessageTemplate,
    MessageTemplateContent,
    saveAsTemplate, SendByActionType,
    sendCampaignTestMessage,
} from "./campaignService";
import {ProviderContext} from "../providerContext";
import Button from "../../components/button/button";
import {ButtonStatus} from "../../components/button/enum";
import {MentionKey, ReplaceableInfo} from "./templateEditor";
import {EditorEmptyRawState} from "./templateEditorDefaultState";
import {differ, formatDate, formatDateTimeToAMPM} from "../../util/util";
import Alert from "../../components/modal/alert";
import {AutomatedCampaignType, CampaignBigType, CampaignType} from "./dataForActiveCampaign";
import {convertFromRaw} from "draft-js";
import {GrayLog} from "../../grayLog/grayLog";
import {LogEventName} from "../../grayLog/logKey";
import {GraylogContext} from "../graylogContext";
import {PromptContext} from "../promptContext";

export enum AppointmentReminderTemplateID {
    primary = "primary",
    followUpWithURL = "follow_up_with_url",
    followUpWithoutURL = "follow_up_without_url",
}

enum CalendarPickerType {
    startDate,
    endDate,
}

interface IProps {
    setting: CampaignSetting,
    dataForTemplateContent: Array<TemplateContent>,
    replaceableInfo?: ReplaceableInfo,
    onBack: () => void,
    onContinue: (setting: CampaignSetting, dataForTemplateContent: Array<TemplateContent>) => void
}

function DesignCampaign(props: IProps) {
    const {replaceableInfo, onContinue, onBack} = props
    const [dataForTemplateContent, setDataForTemplateContent] = useState<Array<TemplateContent>>([])
    const [showCalendarPicker, setShowCalendar] = useState<boolean>(false)
    const [calendarPickerType, setCalendarPickerType] = useState<CalendarPickerType>(CalendarPickerType.startDate)
    const [showSendTestMessageModal, setShowSendTestMessageModal] = useState<boolean>(false)
    const [setting, setSetting] = useState<CampaignSetting>(props.setting)
    const [showSpinner, setShowSpinner] = useState<boolean>(false)
    const [showModal, setShowModal] = useState<boolean>(false)
    const [saveTemplateName, setSaveTemplateName] = useState<string>("")
    const [templateSource, setTemplateSource] = useState<Array<MessageTemplateContent>>(getDefaultCampaignTemplates())
    const [isInvalidSaveTemplateName, setIsInvalidSaveTemplateName] = useState<boolean>(false)
    const [hasTriggerPPEnableEmail, setHasTriggerPPEnableEmail] = useState<boolean>(false)
    const [isDataValid, setIsDataValid] = useState<boolean>(true)

    const provider = useContext(ProviderContext)
    const logEmployee = useContext(GraylogContext)
    const {showSuccess, showError, showAlert} = useContext(PromptContext)

    useEffect(() => {
        props.setting.automatedCampaignType !== AutomatedCampaignType.birthday && getTemplateListById()
    }, [props.setting.automatedCampaignType])

    useEffect(() => {
        setSetting(props.setting)
    }, [props.setting])

    useEffect(() => {
        let templateContent = props.dataForTemplateContent
        if (setting.campaignBigType === CampaignBigType.automatedCampaign) {
            let predefinedLanguages = predefinedTemplateLanguages.slice()
            if (koreanClinics.length && !koreanClinics.includes(provider.clinicID)) {
                predefinedLanguages = predefinedLanguages.filter((lan) => lan !== Language.korean)
            }
            templateContent = props.dataForTemplateContent.filter((item) => predefinedLanguages.includes(item.language))
        }
        setDataForTemplateContent(templateContent)
    }, [props.dataForTemplateContent])

    const getTemplateListById = () => {
        setShowSpinner(true)
        getTemplateList(provider.mEmployeeID, (list) => {
            setShowSpinner(false)
            if (props.setting.automatedCampaignType === AutomatedCampaignType.appointment) {
                setTemplateSource(getDefaultAppointmentReminderTemplates())
            } else {
                setTemplateSource(getDefaultCampaignTemplates().concat(list))
            }
        })
    }

    const $sectionTitle = (title: string) => {
        return (<p className={"font-semibold text-xl text-gray-1 mb-4 mt-6"}>{title}</p>)
    }

    const onchangeCampaignName = (name: string) => {
        setSetting({...setting, campaignName: name})
    }

    const onchangeMessagePerDay = (id: any) => {
        setSetting({...setting, messagePerDay: id})
    }

    const onClickFireType = (id: any) => {
        setSetting({...setting, fireType: id, recurringCampaign: 1, campaignType: CampaignType.sendOnce})
    }

    const onClickIsRecurring = (id: any) => {
        setSetting({
            ...setting,
            recurringCampaign: id,
            campaignType: id === 1 ? CampaignType.sendOnce : CampaignType.sendWeekly,
            endDate: id === 1 ? setting.startDate : moment(setting.startDate).add(6, "month")
                .toDate(),
        })
    }

    const onClickRecurrence = (id: any) => {
        setSetting({...setting, campaignType: id})
    }

    const onClickTemplate = (id: any) => {
        let campaignName = setting.campaignName
        if (id === AppointmentReminderTemplateID.primary) {
            campaignName = "Appointment Reminder - Primary"
        } else if (id === AppointmentReminderTemplateID.followUpWithURL) {
            campaignName = "Appointment Reminder - Follow up"
        } else if (id === AppointmentReminderTemplateID.followUpWithoutURL) {
            campaignName = "Appointment Reminder - Final"
        }
        setSetting({
            ...setting,
            templateId: id,
            campaignName: campaignName,
        })
        updateLanguageContentById(id)
    }

    const updateLanguageContentById = (templateId: string) => {
        const defaultContent = setting.automatedCampaignType === AutomatedCampaignType.appointment ? getDefaultAppointmentDataForTemplateLanguage() : getDefaultDataForTemplateLanguage()
        const targetTemplate = templateSource.find((_template) => {
            return _template.templateId === templateId
        })
        if (targetTemplate) {
            const newTemplateContent = defaultContent.map((contentItem) => {
                let item = Object.assign({}, contentItem)
                let template = targetTemplate.templateContent.find((obj) => obj.language === item.language)
                if (template) {
                    item.compliedMessage = template.compliedMessage
                    item.libMessage = template.libMessage
                }
                return item
            })
            setDataForTemplateContent(newTemplateContent)
        } else {
            setDataForTemplateContent(defaultContent)
        }
    }

    const onRemoveTemplateContent = (language: Language) => {
        const list = dataForTemplateContent.map((_item) => {
            if (_item.language === language) {
                _item.compliedMessage = ""
                _item.libMessage = EditorEmptyRawState
            }
            return _item
        })
        setDataForTemplateContent(list)
    }

    const onFinishInputtingLanguageContent = (libMessage: string, compliedMessage: string, language: Language) => {
        const target = dataForTemplateContent.find((item) => item.language === language)
        if (target?.compliedMessage !== compliedMessage) {
            const data = dataForTemplateContent.map((_item) => {
                if (_item.language === language) {
                    _item.libMessage = libMessage
                    _item.compliedMessage = compliedMessage
                }
                return _item
            })
            setDataForTemplateContent(data)
        }
    }

    const onShowStartDatePicker = () => {
        setCalendarPickerType(CalendarPickerType.startDate)
        setShowCalendar(true)
    }

    const onShowEndDatePicker = () => {
        setCalendarPickerType(CalendarPickerType.endDate)
        setShowCalendar(true)
    }

    const onChangeDate = (date: Date) => {
        setShowCalendar(false)
        if (calendarPickerType === CalendarPickerType.startDate) {
            if (moment(setting.endDate).isBefore(moment(date))) {
                setSetting({...setting, startDate: date, endDate: date})
            } else {
                setSetting({...setting, startDate: date})
            }
        } else {
            setSetting({...setting, endDate: date})
        }
    }

    const checkLanguageContent = () => {
        const content = dataForTemplateContent[0];
        const rawState = JSON.parse(content.libMessage);
        const contentState = convertFromRaw(rawState);
        return contentState.hasText() && content.compliedMessage
    }

    const sendTriggerEmail = () => {
        const testMessage = {
            actionType: SendByActionType.sms,
            email: "digitalhealth@mdland.com",
            mobilePhone: "6612287696",
            title: "text-to-book email trigger",
            message: `${logEmployee.employeeName}(${logEmployee.clinicId}) has triggered this email to add the text-to-book link.`,
            clinicId: provider.clinicID,
            patientId: 0,
            mPatientId: 0,
            RequireCheckAppointment: false,
            isDone: false,
        }
        sendCampaignTestMessage(testMessage, () => {
            setHasTriggerPPEnableEmail(true)
            GrayLog.event(LogEventName.sendTriggerPPEnableEmail, {isSuccessful: true, ...logEmployee})
        }, (error) => {
            GrayLog.event(LogEventName.sendTriggerPPEnableEmail, {isSuccessful: false, errMsg: `${error.code} - ${error.msg}`, ...logEmployee})
        })
    }

    const onContentChanged = (libMessage: string, compliedMessage: string) => {
        if (!provider.clinicOnlineScheduleEnable && !hasTriggerPPEnableEmail && compliedMessage.includes(MentionKey.appointmentLink)) {
            sendTriggerEmail()
        }
    }

    const $getTemplateLanguageContent = () => {
        return (
            <TemplateEditContentList
                title={"Message"}
                setting={setting}
                isDataValid={isDataValid}
                data={dataForTemplateContent}
                replaceableInfo={replaceableInfo}
                onRemove={onRemoveTemplateContent}
                onFinish={onFinishInputtingLanguageContent}
                onChange={onContentChanged}
            />
        )
    }

    const onClickBack = () => {
        onBack && onBack()
    }

    const onClickContinue = () => {
        if (!setting.campaignName.length) {
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: "smooth",
            });
            setIsDataValid(false)
            return
        }

        if (setting.campaignBigType === CampaignBigType.regularCampaign && setting.fireType === FireType.scheduleLater &&
            !validateDateTime(setting.startDate, setting.sendDateTime)) {
            setIsDataValid(false)
            return;
        }

        if (!checkLanguageContent()) {
            showAlert("Message content is required.")
            setIsDataValid(false)
            return;
        }

        if (!provider.clinicOnlineScheduleEnable && dataForTemplateContent.find((item) => item.compliedMessage.includes(MentionKey.appointmentLink))) {
            showAlert("The text-to-book feature is disabled because you haven't enable the provider portal. Please contact iClinicHealth team at digitalhealth@mdland.com")
            setIsDataValid(false)
            return;
        }

        onContinue && onContinue(setting, dataForTemplateContent)
    }

    const onClickSendTestMessage = () => {
        if (!checkLanguageContent()) {
            return
        }
        setShowSendTestMessageModal(true)
    }

    const onCancelSendTestingMessageModal = () => {
        setShowSendTestMessageModal(false)
    }

    const onSendTestingMessageModal = () => {
        setShowSendTestMessageModal(false)
        differ(() => {
            showSuccess("Test message sent")
        }, 1000)
    }

    const onOpenSaveTemplateModal = () => {
        if (!checkLanguageContent()) {
            return
        }
        setShowModal(true)
    }

    const onSaveTemplateByName = (name: string) => {
        const list: Array<MessageTemplate> = dataForTemplateContent.map(({language, compliedMessage, libMessage}) => {
            return {
                language: language,
                libMessage: libMessage,
                compliedMessage: compliedMessage,
            }
        })
        setShowSpinner(true)
        saveAsTemplate("", name, list, () => {
            setShowSpinner(false)
            setSaveTemplateName("")
            differ(() => {
                showSuccess("Your template has been created.")
            }, 1000)
            getTemplateListById()
            GrayLog.event(LogEventName.saveCampaignTemplate, {isSuccessful: true, ...logEmployee})
        }, (error) => {
            setShowSpinner(false)
            showError(error)
            GrayLog.event(LogEventName.saveCampaignTemplate, {isSuccessful: false, errMsg: `${error.code} - ${error.msg}`, ...logEmployee})
        })
    }

    const errMsgForSaveTemplateName = isInvalidSaveTemplateName ? (saveTemplateName.length ? "Template name already exists." : "Template's name is required.") : ""
    const saveTemplateAlert = (
        <Alert
            isOpen={showModal}
            title={"Save as template"}
            description={"Your message will be saved as a template and can be found under “Template”."}
            form={{
                title: "Template Name",
                placeholder: "My custom template",
                value: saveTemplateName,
                invalidMsg: errMsgForSaveTemplateName,
                onChange: (text) => {
                    setSaveTemplateName(text)
                    setIsInvalidSaveTemplateName(false)
                },
            }}
            resolveAction={{
                title: "Save",
                disabled: !saveTemplateName.length,
                onClick: () => {
                    if (!saveTemplateName.length) {
                        return;
                    }
                    if (templateSource.find((item) => item.name === saveTemplateName)) {
                        setIsInvalidSaveTemplateName(true)
                        return;
                    }
                    setShowModal(false)
                    setTimeout(() => {
                        onSaveTemplateByName(saveTemplateName)
                    }, 500)
                },
            }}
            rejectAction={{
                title: "Cancel",
                onClick: () => {
                    setShowModal(false)
                    setSaveTemplateName("")
                },
            }}
        />
    )

    let tipsTitle = "", tipsDescription = ""
    if (setting.automatedCampaignType === AutomatedCampaignType.appointment) {
        tipsTitle = "Appointment Reminder:"
        tipsDescription = "Remind your patients before the appointment date."
    } else if (setting.automatedCampaignType === AutomatedCampaignType.birthday) {
        tipsTitle = "Send a birthday message:"
        tipsDescription = "Send a celebratory message to your patients on their birthday with our data-based automation."
    }
    const $tips = tipsTitle || tipsDescription ? (
        <div className={"flex flex-row items-center mt-2"}>
            <p className={"text-base text-gray-2"}><span className={"font-semibold"}>{tipsTitle}</span> {tipsDescription}</p>
        </div>
    ) : null

    const $title = (
        <Fragment>
            <p className={"font-bold text-2xl"}>Design Campaign</p>
            {$tips}
            <div className={"w-full h-px bg-gray-300 mt-4"}/>
        </Fragment>
    )

    const $fireTypeRadioForm = !setting.campaignId.length ? (<div className={"w-full mt-6"}>
        <RadioForm title={"When do you want to send this campaign?"} data={dataForFireType} id={setting.fireType} onClick={onClickFireType} />
    </div>) : null

    const errMsgForCampaignNameForm = (!isDataValid && !setting.campaignName.length) ? "Campaign name is required" : ""
    const $campaignNameAndMessageMaxPerDayNumber = (
        <div className={"w-full grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-4"}>
            <Form title={"Campaign Name"} errMsg={errMsgForCampaignNameForm} placeholder={"Campaign Name"} value={setting?.campaignName ? setting.campaignName : ""} onChangeText={onchangeCampaignName}/>
            <DropdownListForm title={"Message per Day"} id={setting.messagePerDay} data={dataForMessagePerDay} onChange={onchangeMessagePerDay}/>
        </div>
    )

    const $campaignName = (
        <div className={"w-full"}>
            <Form disabled={setting.automatedCampaignType === AutomatedCampaignType.appointment} title={"Campaign Name"} errMsg={errMsgForCampaignNameForm} placeholder={"Campaign Name"} value={setting?.campaignName ? setting.campaignName : ""} onChangeText={onchangeCampaignName}/>
        </div>
    )

    const $iconCalendar = (
        <div>
            <svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_673_429)">
                    <path d="M16.3214 2.25H14.3929V0.421875C14.3929 0.189844 14.1759 0 13.9107 0H12.3036C12.0384 0 11.8214 0.189844 11.8214 0.421875V2.25H6.67857V0.421875C6.67857 0.189844 6.46161 0 6.19643 0H4.58929C4.32411 0 4.10714 0.189844 4.10714 0.421875V2.25H2.17857C1.11384 2.25 0.25 3.00586 0.25 3.9375V16.3125C0.25 17.2441 1.11384 18 2.17857 18H16.3214C17.3862 18 18.25 17.2441 18.25 16.3125V3.9375C18.25 3.00586 17.3862 2.25 16.3214 2.25ZM16.0804 16.3125H2.41964C2.28705 16.3125 2.17857 16.2176 2.17857 16.1016V5.625H16.3214V16.1016C16.3214 16.2176 16.2129 16.3125 16.0804 16.3125Z" fill="black"/>
                </g>
                <defs>
                    <clipPath id="clip0_673_429">
                        <rect width="18" height="18" fill="white" transform="translate(0.25)"/>
                    </clipPath>
                </defs>
            </svg>
        </div>
    )

    const $regularCampaignDatePicker = (
        <DateTimePicker dateTime={setting.sendDateTime} onChangeValue={(dateTime) => {
            setSetting({
                ...setting,
                sendDateTime: dateTime,
            })
        }}/>
    )

    const $datePickerForStartTime = (
        <Fragment>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 0.28125C4.18359 0.28125 0.28125 4.18359 0.28125 9C0.28125 13.8164 4.18359 17.7188 9 17.7188C13.8164 17.7188 17.7188 13.8164 17.7188 9C17.7188 4.18359 13.8164 0.28125 9 0.28125ZM9 16.0312C5.11523 16.0312 1.96875 12.8848 1.96875 9C1.96875 5.11523 5.11523 1.96875 9 1.96875C12.8848 1.96875 16.0312 5.11523 16.0312 9C16.0312 12.8848 12.8848 16.0312 9 16.0312ZM11.1727 12.3609L8.18789 10.1918C8.07891 10.1109 8.01562 9.98438 8.01562 9.85078V4.07812C8.01562 3.84609 8.20547 3.65625 8.4375 3.65625H9.5625C9.79453 3.65625 9.98438 3.84609 9.98438 4.07812V9.05977L12.3328 10.7684C12.5227 10.9055 12.5613 11.1691 12.4242 11.359L11.7633 12.2695C11.6262 12.4559 11.3625 12.498 11.1727 12.3609Z" fill="#4F4F4F"/>
            </svg>
            {$regularCampaignDatePicker}
        </Fragment>
    )

    const formatStartDate = moment(setting.startDate).format(TimeFormat.MMDDYYYY)
    const formatEndDate = moment(setting.endDate).format(TimeFormat.MMDDYYYY)
    const renderErrMsgForDateTimePicker = () => {
        if (setting.campaignBigType === CampaignBigType.regularCampaign) {
            if (!validateDateTime(setting.startDate, setting.sendDateTime)) {
                return <p className="text-red-500 text-sm italic">{"Must be greater than the current time"}</p>
            }
        }
        // else if (setting.campaignBigType === CampaignBigType.automatedCampaign) {
        //     const startTime = moment(setting.startDate).format(TimeFormat.MMDDYYYY) + " " + setting.sendDateTime
        //     const startMoment = moment(startTime, TimeFormat.MMDDYYYYHHmm)
        //     // auto campaign在start date前1个小时开始准备数据
        //     if (startMoment.isSameOrBefore(moment().add(1, "hour"), "minute") && startMoment.isSameOrAfter(moment(), "minute")) {
        //         return <p className="text-gray-3 text-sm italic">{"The appointment reminder campaign takes 1 hour to process. You campaign will be effective starting tomorrow."}</p>
        //     }
        // }

        return null
    }

    const onClickAppointmentReminderDate = (id: BeforeDayForAutomatedCampaign) => {
        setSetting({
            ...setting,
            beforeDay: id,
        })
    }
    const onClickPostAppointmentDate = (id: BeforeDayForAutomatedCampaign) => {
        setSetting({
            ...setting,
            beforeDay: id,
        })
    }
    const $dropdownForAppointmentReminderStartDate = (
        <DropdownListForm title={"Date"} id={setting.beforeDay} data={dataFoAppointmentReminder} onChange={onClickAppointmentReminderDate}/>
    )
    const $dropdownForPostAppointmentStartDate = (
        <DropdownListForm title={"Date"} id={setting.beforeDay} data={dataForPostAppointment} onChange={onClickPostAppointmentDate}/>
    )

    const $btnFormForRegularCampaignStartDate = (
        (<ButtonForm icon={() => $iconCalendar} title={"Start Date"} value={formatStartDate} placeholder={"Select Date"} onClick={onShowStartDatePicker} />)
    )

    const $btnFormForBirthdayReminderStartDate = (
        (<ButtonForm disabled={true} icon={() => $iconCalendar} title={"Date"} value={"On Patient's Birthday"} />)
    )

    const $startDateForm = () => {
        if (setting.automatedCampaignType === AutomatedCampaignType.appointment) {
            return $dropdownForAppointmentReminderStartDate
        } else if (setting.automatedCampaignType === AutomatedCampaignType.postAppointment) {
            return $dropdownForPostAppointmentStartDate
        } else if (setting.automatedCampaignType === AutomatedCampaignType.birthday) {
            return $btnFormForBirthdayReminderStartDate
        }
        return $btnFormForRegularCampaignStartDate
    }

    const $startDateAndTime = (
        <div className={"grid grid-cols-1 lg:grid-cols-2 gap-x-0 gap-y-2 lg:gap-y-0 lg:gap-x-4 mt-4"}>
            {$startDateForm()}
            <div className={""}>
                <p className={"relative text-gray-1 text-sm text-left mb-1"}>{"Time (EST)"}</p>
                <div className={"flex flex-row items-center bg-gray-6 rounded h-10 px-4 space-x-2"}>
                    {$datePickerForStartTime}
                </div>
                {renderErrMsgForDateTimePicker()}
            </div>
        </div>
    )

    const $recurringRadioForm = (
        <div className={"w-full mt-4"}>
            <RadioForm title={"Is this a recurring campaign?"} data={dataForIsRecurring} id={setting.recurringCampaign} onClick={onClickIsRecurring} />
        </div>
    )

    const $recurrenceTips = () => {
        let tips = ""
        switch (setting.campaignType) {
        case CampaignType.sendOnce:
            tips = `Campaign will be sent to patients at ${formatDateTimeToAMPM(setting.sendDateTime).toLowerCase()} EST starting ${formatDate(moment(setting.startDate).format(TimeFormat.YYYYMMDDHHmmss))}.`
            break
        case CampaignType.sendMonthly:
            tips = `Campaign will be sent to patients every month at ${formatDateTimeToAMPM(setting.sendDateTime).toLowerCase()} EST starting ${formatDate(moment(setting.startDate).format(TimeFormat.YYYYMMDDHHmmss))}.`
            break
        case CampaignType.sendQuarterly:
            tips = `Campaign will be sent to patients every quarter at ${formatDateTimeToAMPM(setting.sendDateTime).toLowerCase()} EST starting ${formatDate(moment(setting.startDate).format(TimeFormat.YYYYMMDDHHmmss))}.`
            break
        default:
            break
        }
        return tips ? <p className={"text-sm text-gray-3 italic my-1"}>{tips}</p> : null
    }

    const $recurrenceFormAndEndDate = setting.recurringCampaign === 0 ? (
        <div className={"grid grid-cols-2 gap-x-4 mt-4"}>
            <DropdownListForm title={"Recurrence"} id={setting.campaignType} data={dataFoRecurrence} onChange={onClickRecurrence}/>
            <ButtonForm icon={() => $iconCalendar} title={"Ending Date"} value={formatEndDate} placeholder={"Select Date"} onClick={onShowEndDatePicker} />
            {$recurrenceTips()}
        </div>
    ) : null

    const $recurrenceOptionsView = !setting.campaignId.length ? (
        <div>
            {$recurringRadioForm}
            {$recurrenceFormAndEndDate}
        </div>
    ) : null
    const $dateTimeSettingForScheduleCampaign = setting.fireType === FireType.scheduleLater ? (
        <Fragment>
            {$startDateAndTime}
            {$recurrenceOptionsView}
        </Fragment>
    ) : null

    const $sectionBasicInfoForRegularCampaign = (
        <Fragment>
            {$sectionTitle("1. Basic Information")}
            {$campaignNameAndMessageMaxPerDayNumber}
            {$fireTypeRadioForm}
            {$dateTimeSettingForScheduleCampaign}
        </Fragment>
    )

    const $sectionBasicInfoForAutomationCampaign = (
        <Fragment>
            {$sectionTitle("1. Basic Information")}
            {$campaignName}
            {$startDateAndTime}
        </Fragment>
    )

    const $sectionBasicInfo = setting.campaignBigType === CampaignBigType.regularCampaign ? $sectionBasicInfoForRegularCampaign : $sectionBasicInfoForAutomationCampaign
    const dataFormTemplateList: Array<DropdownListItem> = useMemo(() => {
        return templateSource.map((_item) => {
            return {
                id: _item.templateId,
                name: _item.name,
            }
        })
    }, [templateSource])
    const $dropdownFormForTemplateList = (setting.automatedCampaignType !== AutomatedCampaignType.birthday ?
        <div className={"mb-4"}>
            <DropdownListForm title={"Template"} id={setting.templateId} data={dataFormTemplateList} onChange={onClickTemplate}/>
        </div> : null
    )

    const sectionGroupForCampaignTemplate = (
        <Fragment>
            {$sectionTitle("2. Campaign Content")}
            {$dropdownFormForTemplateList}
            {$getTemplateLanguageContent()}
        </Fragment>
    )

    const $sendTestMessage = (
        <Button onClick={onClickSendTestMessage} status={ButtonStatus.link} >
            Send test message
        </Button>
    )

    const $saveAsTemplate = setting.campaignBigType === CampaignBigType.regularCampaign ? (
        <Button onClick={onOpenSaveTemplateModal} status={ButtonStatus.link} >
            Save as template
        </Button>
    ) : null

    const $footerButtons = (
        <div className={"flex flex-col md:flex-row items-start md:items-center space-x-4"}>
            {$sendTestMessage}
            {$saveAsTemplate}
        </div>
    )

    const $routeButton = (
        <div className={"w-full flex flex-row items-center justify-between mt-6"}>
            <Button onClick={onClickBack} status={ButtonStatus.light} >
                Back
            </Button>

            <Button onClick={onClickContinue} status={ButtonStatus.primary} >
                Continue
            </Button>
        </div>
    )

    const $footer = (
        <div className={"mt-5"}>
            {$footerButtons}
            {$routeButton}
        </div>
    )

    const $footerModal = (
        <SendTestingMessageModal messageTemplates={dataForTemplateContent} isShow={showSendTestMessageModal} isPostAppointment= {setting.campaignBigType === CampaignBigType.automatedCampaign && setting.automatedCampaignType === AutomatedCampaignType.postAppointment} onCancel={onCancelSendTestingMessageModal} onSend={onSendTestingMessageModal} />
    )

    const $CalendarPicker = (
        <CalendarPicker
            isOpen={showCalendarPicker}
            value={calendarPickerType === CalendarPickerType.startDate ? setting.startDate : setting.endDate }
            maxDate={new Date(3000, 1, 1)}
            minDate={calendarPickerType === CalendarPickerType.startDate ? (new Date()) : setting.startDate}
            onSelectDate={onChangeDate}
            onClose={() => {
                setShowCalendar(false)
            }}
        />
    )

    const $spinner = <Spinner isOpen={showSpinner} />
    return <div className={"w-full p-6 lg:p-10 bg-white shadow-lg rounded-lg"}>
        {$title}
        {$sectionBasicInfo}
        {sectionGroupForCampaignTemplate}
        {$footer}
        {$footerModal}
        {$CalendarPicker}
        {saveTemplateAlert}
        {$spinner}
    </div>
}

export default DesignCampaign
